import React, { useState, useRef, useEffect } from "react";
import { View, StyleSheet } from "react-native";
import { useRouter } from "expo-router";
import { ProjectType } from "@/types/db/projects";
import { ClientType } from "@/types/db/clients";
import { ShiftStatusType } from "@/types/db/shifts";
import {
  ShiftCreateForm,
  ShiftCreateFormRef,
  ShiftPayload,
  SHIFT_STATUS_OPTIONS,
  getShiftStatusColor,
} from "@/components/screens/shift/NewShiftForm";
import { CreateHeader } from "@/components/screens/shared";
import useToast from "@/hooks/useToast";
import { shiftsService } from "@/services/shiftService";
import Head from "expo-router/head";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { useTheme } from "@/hooks/useTheme";
import { getSaveBehavior } from "@/lib/saveSettings";

export default function NewShiftPage() {
  const toast = useToast();
  const router = useRouter();
  const { triggerRefresh } = useRowSelectionContext();
  const [localDraft, setLocalDraft] = useState<ShiftPayload>();
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectType | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientType | null>(null);
  const [shiftName, setShiftName] = useState("");
  const [shiftDescription, setShiftDescription] = useState("");
  const [shiftStatus, setShiftStatus] = useState<ShiftStatusType>("PENDING");
  const { colors } = useTheme();
  const formRef = useRef<ShiftCreateFormRef>(null);

  const handleCreateShift = async (payload: ShiftPayload) => {
    if (!payload.shift.project_id) {
      toast.showToast({ title: "Project required", message: "Select a project first.", toastType: "warning" });
      return;
    }
    setLoading(true);
    try {
      const newShift = await shiftsService.createShift({
        project_id: payload.shift.project_id,
        name: payload.shift.name || undefined,
        description: payload.shift.description || undefined,
        start_time: payload.shift.start_time,
        end_time: payload.shift.end_time,
        status: payload.shift.status,
        meta_data: payload.shift.meta_data,
      });
      toast.showToast({ title: "Shift created successfully", toastType: "success" });
      triggerRefresh("shift");

      // Handle save behavior setting
      const behavior = await getSaveBehavior("shift");
      switch (behavior) {
        case "view_created":
          router.setParams({ new: undefined, id: newShift?.id });
          break;
        case "create_another":
          handleReset();
          break;
        case "close":
        default:
          router.setParams({ new: undefined, panel: undefined });
          break;
      }
    } catch (err: any) {
      toast.showToast({
        title: "Failed to create shift",
        message: err.message,
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setShiftName("");
    setShiftDescription("");
    setShiftStatus("PENDING");
    setSelectedProject(null);
    setSelectedClient(null);
    formRef.current?.handleReset();
  };

  // Build subtitle: show project name if selected, else client name
  const subtitle = selectedProject?.name ?? selectedClient?.name;

  return (
    <>
      <Head>
        <title>NEXUS New Shift</title>
        <meta name="description" content="" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="shift"
          name={shiftName}
          onNameChange={(name) => {
            setShiftName(name);
            formRef.current?.updateShiftName(name);
          }}
          namePlaceholder="Shift name *"
          description={shiftDescription}
          onDescriptionChange={(desc) => {
            setShiftDescription(desc);
            formRef.current?.updateShiftDescription(desc);
          }}
          descriptionPlaceholder="Shift description"
          status={shiftStatus}
          onStatusChange={(status) => {
            setShiftStatus(status as ShiftStatusType);
            formRef.current?.updateShiftStatus(status);
          }}
          statusOptions={SHIFT_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getShiftStatusColor(opt.text, colors),
          }))}
          dirty={dirty}
          saving={loading}
          onSave={async () => {
            await formRef.current?.handleSave();
          }}
          onCancel={handleReset}
          subtitle={subtitle}
        />
        <View style={{ flex: 1, padding: 16 }}>
          <ShiftCreateForm
            ref={formRef}
            initialShift={{ name: shiftName, description: shiftDescription, status: shiftStatus }}
            onChange={(current) => {
              setLocalDraft(current);
            }}
            onSave={handleCreateShift}
            onDirtyChange={setDirty}
            onProjectChange={setSelectedProject}
            onClientChange={setSelectedClient}
          />
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  centered: {
    paddingVertical: 32,
    alignItems: "center",
    gap: 8,
  },
});
